import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import Badge from "../ui/badge/Badge";
import MediaUploader from '../common/MediaUploader';
import MediaPickerModal from '../common/MediaPickerModal';

interface LinkItem {
    title: string;
    url: string;
}

function CMSFooterLinkList({ title, items, onChange }: { title: string, items: LinkItem[], onChange: (items: LinkItem[]) => void }) {
    const [newItem, setNewItem] = useState<LinkItem>({ title: '', url: '' });

    const addItem = () => {
        if (!newItem.title || !newItem.url) return;
        onChange([...items, newItem]);
        setNewItem({ title: '', url: '' });
    };

    const removeItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange(newItems);
    };

    return (
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
            <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">{title}</h4>

            <div className="space-y-3 mb-4">
                {items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                        <div className="flex-1 grid grid-cols-2 gap-2 text-sm">
                            <span className="truncate text-gray-700 dark:text-gray-300">{item.title}</span>
                            <span className="truncate text-gray-500">{item.url}</span>
                        </div>
                        <button
                            onClick={() => removeItem(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                            type="button"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Title"
                    className="flex-1 w-full sm:w-[40%] rounded-md border border-gray-300 bg-transparent px-3 py-1.5 text-sm text-gray-800 dark:text-white"
                />
                <input
                    type="text"
                    value={newItem.url}
                    onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                    placeholder="URL"
                    className="flex-1 w-full sm:w-[40%] rounded-md border border-gray-300 bg-transparent px-3 py-1.5 text-sm text-gray-800 dark:text-white"
                />
                <button
                    type="button"
                    onClick={addItem}
                    disabled={!newItem.title || !newItem.url}
                    className="sm:w-auto w-full rounded-md bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
                >
                    Add
                </button>
            </div>
        </div>
    );
}

export default function CMSFooters() {
    const [footers, setFooters] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState({
        content: '',
        active: true,
        information: [] as LinkItem[],
        customer_service: [] as LinkItem[],
        my_account: [] as LinkItem[],
        social_media: [] as LinkItem[],
        image_id: null as number | null,
        image_preview: null as string | null
    });
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

    useEffect(() => {
        fetchFooters();
    }, []);

    const fetchFooters = async () => {
        try {
            const res = await client.getFrontFooters() as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setFooters(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch footers:', err);
            setFooters([]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.content) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Content is required',
                icon: 'warning'
            });
            return;
        }
        setLoading(true);
        try {
            const payload = {
                id: editingId,
                content: form.content,
                active: form.active,
                information: form.information as any[],
                customer_service: form.customer_service as any[],
                my_account: form.my_account as any[],
                social_media: form.social_media as any[],
                images: form.image_id ? [form.image_id] : undefined
            };

            if (editingId) {
                await (client as any).postV1FooterUpdate({
                    postV1FooterUpdateRequest: payload
                });
            } else {
                await client.postV1FooterCreate({
                    postV1FooterCreateRequest: payload
                } as any);
            }
            setIsModalOpen(false);
            setForm({
                content: '',
                active: true,
                information: [],
                customer_service: [],
                my_account: [],
                social_media: [],
                image_id: null,
                image_preview: null
            });
            setEditingId(null);
            fetchFooters();
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: editingId ? 'Footer updated successfully' : 'Footer created successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err: any) {
            console.error(err);
            Swal.fire({
                title: 'Error',
                text: err?.response?.data?.message || err?.message || 'Failed to save footer',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingId(null);
        setForm({
            content: '',
            active: true,
            information: [],
            customer_service: [],
            my_account: [],
            social_media: [],
            image_id: null,
            image_preview: null
        });
        setIsModalOpen(true);
    };

    const handleEdit = (footer: any) => {
        setEditingId(footer.id);
        const firstImage = footer.images && footer.images.length > 0 ? footer.images[0] : null;
        setForm({
            content: footer.content || '',
            active: !!footer.active,
            information: footer.information || [],
            customer_service: footer.customer_service || [],
            my_account: footer.my_account || [],
            social_media: footer.social_media || [],
            image_id: firstImage ? (typeof firstImage === 'object' ? firstImage.id : firstImage) : null,
            image_preview: firstImage && typeof firstImage === 'object' ? firstImage.path : null
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await client.postV1FooterDelete({
                    postV1FooterDeleteRequest: { id: id }
                } as any);
                fetchFooters();
                Swal.fire({
                    title: 'Deleted!',
                    text: 'Footer deleted successfully',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } catch (err) {
                console.error('Failed to delete footer:', err);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete footer',
                    icon: 'error'
                });
            }
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Footers</h3>
                <div className="flex items-center gap-2">
                    {loading && <span className="text-sm text-gray-500">Loading...</span>}
                    <button
                        onClick={handleCreate}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm"
                    >
                        Create Footer
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Image</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Content</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {footers.length === 0 ? (
                            <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center">No footers found.</TableCell></TableRow>
                        ) : (
                            footers.map(footer => (
                                <TableRow key={footer.id}>
                                    <TableCell className="px-5 py-4">
                                        {footer.images && footer.images.length > 0 && typeof footer.images[0] === 'object' && footer.images[0].path ? (
                                            <img
                                                src={footer.images[0].path.startsWith('http')
                                                    ? footer.images[0].path
                                                    : `${import.meta.env.VITE_API_BASE || 'http://localhost:8000'}/${footer.images[0].path.replace(/^\//, '')}`}
                                                alt=""
                                                className="w-10 h-10 object-cover rounded shadow-sm border border-gray-100 dark:border-gray-800"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center text-[10px] text-gray-400">No Image</div>
                                        )}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 font-medium text-gray-900 dark:text-white">
                                        <span className="line-clamp-2 max-w-xs">{footer.content || '-'}</span>
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        <Badge color={footer.active ? 'success' : 'error'} size="sm">
                                            {footer.active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(footer)}
                                                className="text-blue-600 hover:underline"
                                                title="Edit Footer"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(footer.id)}
                                                className="text-red-600 hover:underline"
                                                title="Delete Footer"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-4xl" showCloseButton={false}>
                <div className="flex flex-col max-h-[90vh] bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
                    {/* Fixed Header */}
                    <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                            {editingId ? 'Edit Footer' : 'Create Footer'}
                        </h3>
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <form id="cms-footer-form" onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                    Content <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={form.content}
                                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none min-h-[120px] text-sm"
                                    placeholder="Enter footer content text..."
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <CMSFooterLinkList
                                    title="Information"
                                    items={form.information}
                                    onChange={(items) => setForm({ ...form, information: items })}
                                />
                                <CMSFooterLinkList
                                    title="Customer Service"
                                    items={form.customer_service}
                                    onChange={(items) => setForm({ ...form, customer_service: items })}
                                />
                                <CMSFooterLinkList
                                    title="My Account"
                                    items={form.my_account}
                                    onChange={(items) => setForm({ ...form, my_account: items })}
                                />
                                <CMSFooterLinkList
                                    title="Social Media"
                                    items={form.social_media}
                                    onChange={(items) => setForm({ ...form, social_media: items })}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <MediaUploader
                                        label="Footer Logo/Image"
                                        initialImages={form.image_id ? [form.image_id] : []}
                                        onUpload={(ids) => {
                                            setForm({ ...form, image_id: ids[0] || null });
                                        }}
                                        multiple={false}
                                        folder="footers"
                                    />
                                    {form.image_id && (
                                        <div className="mt-2 p-2 border border-gray-100 dark:border-gray-800 rounded-xl bg-gray-50/50 dark:bg-gray-800/50">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 px-1">Current Selection</p>
                                            <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                                                {form.image_preview ? (
                                                    <img
                                                        src={form.image_preview.startsWith('http')
                                                            ? form.image_preview
                                                            : `${import.meta.env.VITE_API_BASE || 'http://localhost:8000'}/${form.image_preview.replace(/^\//, '')}`}
                                                        className="w-full h-full object-cover"
                                                        alt="Preview"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-900 text-[10px] text-gray-400">ID: {form.image_id}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Select from Media Library</span>
                                        <button
                                            type="button"
                                            onClick={() => setIsMediaModalOpen(true)}
                                            className="text-sm text-brand-500 hover:text-brand-600 font-bold transition-colors"
                                        >
                                            Open Library
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                                    <select
                                        value={form.active ? 'true' : 'false'}
                                        onChange={e => setForm({ ...form, active: e.target.value === 'true' })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none cursor-pointer appearance-none text-sm"
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Fixed Footer */}
                    <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3 bg-gray-50/50 dark:bg-gray-800/30">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-6 py-2.5 text-sm font-bold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-bold"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="cms-footer-form"
                            className="px-6 py-2.5 text-sm font-bold text-white bg-brand-500 border border-brand-500 rounded-xl hover:bg-brand-600 shadow-[0_4px_12px_rgba(59,130,246,0.3)] transition-all active:scale-95 font-bold"
                        >
                            {editingId ? 'Update Footer' : 'Create Footer'}
                        </button>
                    </div>
                </div>
            </Modal>

            <MediaPickerModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                onSelect={(file) => {
                    setForm({ ...form, image_id: file.id, image_preview: file.path || null });
                    setIsMediaModalOpen(false);
                }}
            />
        </div>
    );
}
